import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Car, Fuel, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, FileText } from 'lucide-react';

interface Reservation {
  id: string;
  nome: string;
  veiculo: string;
  empresa: string;
  telefone: string;
  data_retirada: string;
  horario_retirada: string;
  data_entrega: string;
  horario_entrega: string;
  status: string;
}

const niveisComb = [
  { value: 'vazio', label: 'Vazio', bars: 0 },
  { value: '1/4', label: '1/4', bars: 1 },
  { value: '1/2', label: '1/2', bars: 2 },
  { value: '3/4', label: '3/4', bars: 3 },
  { value: 'cheio', label: 'Cheio', bars: 4 },
];

export function PublicChecklist() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [alreadyFilled, setAlreadyFilled] = useState(false);
  const [form, setForm] = useState({
    km_saida: '',
    nivel_combustivel: '1/2',
    pneus_ok: false,
    documentacao_ok: false,
    farois_ok: false,
    pisca_alerta_ok: false,
    luz_freio_ok: false,
    avarias: '',
    observacoes: '',
    nome_condutor: '',
  });

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const { data, error: fetchError } = await supabase
          .from('reserva_veiculos')
          .select('*')
          .eq('checklist_token', token)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (!data) {
          setError('Token de checklist inválido ou expirado.');
          return;
        }

        if (data.status === 'approved') {
          setAlreadyFilled(true);
          setReservation(data);
          return;
        }

        if (data.status !== 'aguardando_checklist') {
          setError('Esta reserva não está aguardando preenchimento de checklist.');
          return;
        }

        setReservation(data);
        setForm(prev => ({ ...prev, nome_condutor: data.nome || '' }));
      } catch (err: any) {
        console.error('[PUBLIC-CHECKLIST] Error:', err);
        setError('Erro ao carregar checklist. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reservation) return;

    if (!form.km_saida) {
      setError('Informe o KM do veículo na saída.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const checklistPayload = {
        km_saida: parseInt(form.km_saida),
        nivel_combustivel: form.nivel_combustivel,
        pneus_ok: form.pneus_ok,
        documentacao_ok: form.documentacao_ok,
        farois_ok: form.farois_ok,
        pisca_alerta_ok: form.pisca_alerta_ok,
        luz_freio_ok: form.luz_freio_ok,
        avarias: form.avarias || null,
        observacoes: form.observacoes || null,
        nome_condutor: form.nome_condutor || null,
      };

      const { error: checklistError } = await supabase
        .from('veiculo_checklist')
        .insert({
          reserva_id: reservation.id,
          ...checklistPayload,
        });

      if (checklistError) throw checklistError;

      const { error: statusError } = await supabase
        .from('reserva_veiculos')
        .update({
          status: 'approved',
          checklist_data: checklistPayload,
        })
        .eq('id', reservation.id);

      if (statusError) throw statusError;

      setSuccess(true);
    } catch (err: any) {
      console.error('[PUBLIC-CHECKLIST] Submit error:', err);
      setError('Erro ao enviar checklist: ' + (err.message || 'Tente novamente'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando checklist...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Checklist enviado!</h1>
          <p className="text-slate-600">
            Seu checklist foi registrado com sucesso. O veículo está liberado para retirada.
          </p>
        </div>
      </div>
    );
  }

  if (alreadyFilled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Checklist ja preenchido</h1>
          <p className="text-slate-600">
            O checklist desta reserva ja foi preenchido e o veiculo foi liberado.
          </p>
        </div>
      </div>
    );
  }

  if (error && !reservation) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Checklist indisponivel</h1>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="mx-auto w-full max-w-3xl">
        <div className="rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Checklist de Saida</h1>
              <p className="text-slate-500 text-sm">Preencha os dados para liberar o veiculo</p>
            </div>
          </div>
        </div>

        {reservation && (
          <div className="rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Dados da reserva</h2>
            <div className="grid gap-3 md:grid-cols-2 text-sm text-slate-700">
              <p><span className="font-medium text-slate-500">Solicitante:</span> {reservation.nome}</p>
              <p><span className="font-medium text-slate-500">Veiculo:</span> {reservation.veiculo}</p>
              <p><span className="font-medium text-slate-500">Empresa:</span> {reservation.empresa || '-'}</p>
              <p><span className="font-medium text-slate-500">Telefone:</span> {reservation.telefone || '-'}</p>
              <p><span className="font-medium text-slate-500">Retirada:</span> {reservation.data_retirada} as {reservation.horario_retirada?.slice(0, 5)}</p>
              <p><span className="font-medium text-slate-500">Devolucao:</span> {reservation.data_entrega} as {reservation.horario_entrega?.slice(0, 5)}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="rounded-2xl bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 shadow-sm p-6">
          <div className="grid gap-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Nome do condutor *
              </label>
              <input
                type="text"
                value={form.nome_condutor}
                onChange={(e) => setForm({ ...form, nome_condutor: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="Nome completo"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                KM atual do veiculo *
              </label>
              <input
                type="number"
                value={form.km_saida}
                onChange={(e) => setForm({ ...form, km_saida: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="Ex: 45823"
                min={0}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                <span className="flex items-center gap-2">
                  <Fuel className="w-4 h-4 text-amber-500" />
                  Nivel de Combustivel *
                </span>
              </label>
              <div className="grid grid-cols-5 gap-2">
                {niveisComb.map((n) => (
                  <button
                    key={n.value}
                    type="button"
                    onClick={() => setForm({ ...form, nivel_combustivel: n.value })}
                    className={`py-2.5 px-1 rounded-xl border-2 text-center transition-all ${
                      form.nivel_combustivel === n.value
                        ? 'border-amber-500 bg-amber-50 text-amber-700 font-semibold'
                        : 'border-slate-200 text-slate-600 hover:border-amber-300'
                    }`}
                  >
                    <div className="flex justify-center gap-0.5 mb-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`w-1.5 h-3 rounded-sm ${
                            i < n.bars ? 'bg-amber-500' : 'bg-slate-200'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs">{n.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-semibold text-slate-700">Condicoes do Veiculo</label>
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.pneus_ok ? 'bg-green-500 border-green-500' : 'border-slate-300'
                  }`}
                  onClick={() => setForm({ ...form, pneus_ok: !form.pneus_ok })}
                >
                  {form.pneus_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.pneus_ok}
                  onChange={(e) => setForm({ ...form, pneus_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-slate-700">Pneus em boas condicoes</span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.documentacao_ok ? 'bg-green-500 border-green-500' : 'border-slate-300'
                  }`}
                  onClick={() => setForm({ ...form, documentacao_ok: !form.documentacao_ok })}
                >
                  {form.documentacao_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.documentacao_ok}
                  onChange={(e) => setForm({ ...form, documentacao_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-slate-700 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  Documentacao presente (CRLV, CNH, etc.)
                </span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.farois_ok ? 'bg-green-500 border-green-500' : 'border-slate-300'
                  }`}
                  onClick={() => setForm({ ...form, farois_ok: !form.farois_ok })}
                >
                  {form.farois_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.farois_ok}
                  onChange={(e) => setForm({ ...form, farois_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-slate-700">Farois funcionando (baixo/alto)</span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.pisca_alerta_ok ? 'bg-green-500 border-green-500' : 'border-slate-300'
                  }`}
                  onClick={() => setForm({ ...form, pisca_alerta_ok: !form.pisca_alerta_ok })}
                >
                  {form.pisca_alerta_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.pisca_alerta_ok}
                  onChange={(e) => setForm({ ...form, pisca_alerta_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-slate-700">Pisca alerta (luzes de emergencia) funcionando</span>
              </label>

              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                    form.luz_freio_ok ? 'bg-green-500 border-green-500' : 'border-slate-300'
                  }`}
                  onClick={() => setForm({ ...form, luz_freio_ok: !form.luz_freio_ok })}
                >
                  {form.luz_freio_ok && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={form.luz_freio_ok}
                  onChange={(e) => setForm({ ...form, luz_freio_ok: e.target.checked })}
                  className="hidden"
                />
                <span className="text-sm text-slate-700">Luzes de freio funcionando</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <span className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  Avarias observadas
                </span>
              </label>
              <textarea
                value={form.avarias}
                onChange={(e) => setForm({ ...form, avarias: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none outline-none"
                rows={2}
                placeholder="Descreva qualquer avaria existente no veiculo"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Observacoes adicionais</label>
              <textarea
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none outline-none"
                rows={2}
                placeholder="Outras observacoes relevantes..."
              />
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              {saving ? 'Enviando...' : 'Confirmar e Liberar Veiculo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
